/*
 * © 2025 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateVersioningSchemaFailError = exports.ImageCollectionRetrievalFailedError = exports.PropertiesRetrievalFailedError = exports.ImageUpdateFailedError = exports.ImageDoesNotExistError = exports.ImageNotFoundError = exports.VersioningSchemaInapplicableError = exports.VersioningSchemaMissingError = exports.ProjectAlreadyLinkedError = exports.UpdateVersioningSchemaFailError = exports.UnableToSortByVersionError = exports.InvalidPaginationCursorError = exports.RequestBodyAttributesMissingError = exports.RequestIdsDoNotMatchError = exports.ProjectDoesNotBelongToGroupError = exports.ProjectIsNotContainerImageError = exports.ProjectDoesNotExistError = exports.RequiredParameterNotProvidedError = exports.VersioningSchemaDoesNotSupportTagError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name VersioningSchemaDoesNotSupportTagError
 * @description The versioning schema used does not support the given tag. Update the versioning schema to include the tag.
 *
 * Once the tag of the custom base image is correct, the versioning schema must be modified.
 * You can use a different versioning schema that supports all tags in the repository or you can update the relevant properties of the versioning schema.
 *
 * For example, if the repository currently uses Semver, and a new tag "1.2.5.7" needs to be added, then you can use a Custom versioning schema.
 *
 * See more:
 * - [https://docs.snyk.io/scan-using-snyk/snyk-container/use-snyk-container-from-the-web-ui/use-custom-base-image-recommendations/versioning-schema-for-custom-base-images](https://docs.snyk.io/scan-using-snyk/snyk-container/use-snyk-container-from-the-web-ui/use-custom-base-image-recommendations/versioning-schema-for-custom-base-images)
 * @summary Versioning schema does not support tag
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class VersioningSchemaDoesNotSupportTagError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Versioning schema does not support tag',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0001',
            status: 400,
            description: `The versioning schema used does not support the given tag. Update the versioning schema to include the tag.

Once the tag of the custom base image is correct, the versioning schema must be modified.
You can use a different versioning schema that supports all tags in the repository or you can update the relevant properties of the versioning schema.

For example, if the repository currently uses Semver, and a new tag "1.2.5.7" needs to be added, then you can use a Custom versioning schema.`,
            errorCode: 'SNYK-CBI-0001',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-using-snyk/snyk-container/use-snyk-container-from-the-web-ui/use-custom-base-image-recommendations/versioning-schema-for-custom-base-images',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.VersioningSchemaDoesNotSupportTagError = VersioningSchemaDoesNotSupportTagError;
/**
 * @class
 * @name RequiredParameterNotProvidedError
 * @description Provide an ORG ID or GROUP ID.
 * @summary Missing required parameter
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class RequiredParameterNotProvidedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Missing required parameter',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0002',
            status: 400,
            description: `Provide an ORG ID or GROUP ID.`,
            errorCode: 'SNYK-CBI-0002',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.RequiredParameterNotProvidedError = RequiredParameterNotProvidedError;
/**
 * @class
 * @name ProjectDoesNotExistError
 * @description The project could not be found. Check that the project exists, that you have access to the project, and also check that the ID you have provided is the project ID and not a CBI ID.
 * @summary Project does not exist
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ProjectDoesNotExistError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Project does not exist',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0003',
            status: 404,
            description: `The project could not be found. Check that the project exists, that you have access to the project, and also check that the ID you have provided is the project ID and not a CBI ID.`,
            errorCode: 'SNYK-CBI-0003',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ProjectDoesNotExistError = ProjectDoesNotExistError;
/**
 * @class
 * @name ProjectIsNotContainerImageError
 * @description The project is not a container image.
 *
 * See more:
 * - [https://docs.snyk.io/scan-using-snyk/snyk-container/use-snyk-container-from-the-web-ui/use-custom-base-image-recommendations](https://docs.snyk.io/scan-using-snyk/snyk-container/use-snyk-container-from-the-web-ui/use-custom-base-image-recommendations)
 * @summary Project is not a container image
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ProjectIsNotContainerImageError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Project is not a container image',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0004',
            status: 400,
            description: `The project is not a container image.`,
            errorCode: 'SNYK-CBI-0004',
            level: 'warn',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/scan-using-snyk/snyk-container/use-snyk-container-from-the-web-ui/use-custom-base-image-recommendations',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ProjectIsNotContainerImageError = ProjectIsNotContainerImageError;
/**
 * @class
 * @name ProjectDoesNotBelongToGroupError
 * @description The project's org does not belong to a group. In order to use a Custom Base Image, recreate the project and add it to a group or add a group to the org. Note that the group feature is not available to free users.
 * @summary Unable to retrieve group
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ProjectDoesNotBelongToGroupError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to retrieve group',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0005',
            status: 400,
            description: `The project's org does not belong to a group. In order to use a Custom Base Image, recreate the project and add it to a group or add a group to the org. Note that the group feature is not available to free users.`,
            errorCode: 'SNYK-CBI-0005',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ProjectDoesNotBelongToGroupError = ProjectDoesNotBelongToGroupError;
/**
 * @class
 * @name RequestIdsDoNotMatchError
 * @description The request body ID and the request path ID do not match. Ensure that the values are the same and try again.
 * @summary The values in the request do not match
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class RequestIdsDoNotMatchError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'The values in the request do not match',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0006',
            status: 400,
            description: `The request body ID and the request path ID do not match. Ensure that the values are the same and try again.`,
            errorCode: 'SNYK-CBI-0006',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.RequestIdsDoNotMatchError = RequestIdsDoNotMatchError;
/**
 * @class
 * @name RequestBodyAttributesMissingError
 * @description The request body does not contain any attributes that can be updated. Provide the necessary attributes and try again.
 * @summary The request body cannot be updated
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class RequestBodyAttributesMissingError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'The request body cannot be updated',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0007',
            status: 400,
            description: `The request body does not contain any attributes that can be updated. Provide the necessary attributes and try again.`,
            errorCode: 'SNYK-CBI-0007',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.RequestBodyAttributesMissingError = RequestBodyAttributesMissingError;
/**
 * @class
 * @name InvalidPaginationCursorError
 * @description The provided pagination cursor is invalid.
 * @summary Invalid pagination cursor
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class InvalidPaginationCursorError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Invalid pagination cursor',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0008',
            status: 400,
            description: `The provided pagination cursor is invalid.`,
            errorCode: 'SNYK-CBI-0008',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.InvalidPaginationCursorError = InvalidPaginationCursorError;
/**
 * @class
 * @name UnableToSortByVersionError
 * @description Snyk was unable to filter by version. Provide a repository filter and try again.
 * @summary Unable to sort by version
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UnableToSortByVersionError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to sort by version',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0009',
            status: 400,
            description: `Snyk was unable to filter by version. Provide a repository filter and try again.`,
            errorCode: 'SNYK-CBI-0009',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UnableToSortByVersionError = UnableToSortByVersionError;
/**
 * @class
 * @name UpdateVersioningSchemaFailError
 * @description The versioning schema could not be applied to all images in the repository. Therefore, no resources have been updated. Update the provided versioning schema so that all tags in the repository fit the new schema.
 * @summary Unable to update versioning schema
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UpdateVersioningSchemaFailError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to update versioning schema',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0010',
            status: 400,
            description: `The versioning schema could not be applied to all images in the repository. Therefore, no resources have been updated. Update the provided versioning schema so that all tags in the repository fit the new schema.`,
            errorCode: 'SNYK-CBI-0010',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UpdateVersioningSchemaFailError = UpdateVersioningSchemaFailError;
/**
 * @class
 * @name ProjectAlreadyLinkedError
 * @description The project ID provided is already linked to another Custom Base Image.
 * @summary Project is already linked to a custom base image
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ProjectAlreadyLinkedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Project is already linked to a custom base image',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0011',
            status: 400,
            description: `The project ID provided is already linked to another Custom Base Image.`,
            errorCode: 'SNYK-CBI-0011',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ProjectAlreadyLinkedError = ProjectAlreadyLinkedError;
/**
 * @class
 * @name VersioningSchemaMissingError
 * @description No versioning schema exists for the repository. This image is the first in its repository. Provide a versioning schema that fits the format of current and future images in this repository.
 * @summary No versioning schema for repository
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class VersioningSchemaMissingError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'No versioning schema for repository',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0012',
            status: 400,
            description: `No versioning schema exists for the repository. This image is the first in its repository. Provide a versioning schema that fits the format of current and future images in this repository.`,
            errorCode: 'SNYK-CBI-0012',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.VersioningSchemaMissingError = VersioningSchemaMissingError;
/**
 * @class
 * @name VersioningSchemaInapplicableError
 * @description A versioning schema already exists for repository. Remove the "versioning_schema" property or, if you want to update the versioning schema, use the PATCH endpoint.
 * @summary Unable to apply versioning schema
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class VersioningSchemaInapplicableError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to apply versioning schema',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0013',
            status: 400,
            description: `A versioning schema already exists for repository. Remove the "versioning_schema" property or, if you want to update the versioning schema, use the PATCH endpoint.`,
            errorCode: 'SNYK-CBI-0013',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.VersioningSchemaInapplicableError = VersioningSchemaInapplicableError;
/**
 * @class
 * @name ImageNotFoundError
 * @description Unable to find the requested custom base image. Try again, and if the error persists, contact Snyk support.
 * @summary Unable to find custom base image
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ImageNotFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to find custom base image',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0014',
            status: 404,
            description: `Unable to find the requested custom base image. Try again, and if the error persists, contact Snyk support.`,
            errorCode: 'SNYK-CBI-0014',
            level: 'warn',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ImageNotFoundError = ImageNotFoundError;
/**
 * @class
 * @name ImageDoesNotExistError
 * @description The requested custom base image does not exist.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Custom base image does not exist
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ImageDoesNotExistError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Custom base image does not exist',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0015',
            status: 404,
            description: `The requested custom base image does not exist.`,
            errorCode: 'SNYK-CBI-0015',
            level: 'warn',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ImageDoesNotExistError = ImageDoesNotExistError;
/**
 * @class
 * @name ImageUpdateFailedError
 * @description An internal error occurred while trying to update a custom base image. Try again, and if the error persists, contact Snyk support.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Unable to update custom base image
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ImageUpdateFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to update custom base image',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0016',
            status: 500,
            description: `An internal error occurred while trying to update a custom base image. Try again, and if the error persists, contact Snyk support.`,
            errorCode: 'SNYK-CBI-0016',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ImageUpdateFailedError = ImageUpdateFailedError;
/**
 * @class
 * @name PropertiesRetrievalFailedError
 * @description An internal error occurred while trying to retrieve project properties. Try again, and if the error persists, contact Snyk support.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Unable to retrieve project properties
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class PropertiesRetrievalFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to retrieve project properties',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0017',
            status: 500,
            description: `An internal error occurred while trying to retrieve project properties. Try again, and if the error persists, contact Snyk support.`,
            errorCode: 'SNYK-CBI-0017',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.PropertiesRetrievalFailedError = PropertiesRetrievalFailedError;
/**
 * @class
 * @name ImageCollectionRetrievalFailedError
 * @description An internal error occurred while trying to retrieve the image collection. Try again, and if the error persists, contact Snyk support.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Unable to retrieve image collection
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ImageCollectionRetrievalFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to retrieve image collection',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0018',
            status: 500,
            description: `An internal error occurred while trying to retrieve the image collection. Try again, and if the error persists, contact Snyk support.`,
            errorCode: 'SNYK-CBI-0018',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ImageCollectionRetrievalFailedError = ImageCollectionRetrievalFailedError;
/**
 * @class
 * @name CreateVersioningSchemaFailError
 * @description The provided versioning schema is invalid and image could therefor not be created. Provide a properly formatted versioning schema and try again.
 * @summary Unable to create versioning schema
 * @category CustomBaseImages
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class CreateVersioningSchemaFailError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to create versioning schema',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cbi-0019',
            status: 400,
            description: `The provided versioning schema is invalid and image could therefor not be created. Provide a properly formatted versioning schema and try again.`,
            errorCode: 'SNYK-CBI-0019',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.CreateVersioningSchemaFailError = CreateVersioningSchemaFailError;
//# sourceMappingURL=CustomBaseImages-error-catalog.js.map